import type {
  BookingProposalItemRecord,
  BookingProposalRecord,
  Currency,
  MoneyRecord,
  ShoppingCartRecord
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { catalogPriceMapKey, getCarDisplayName, type IncludedOrderLine, type ProposalCatalogPrice } from 'noddi-util';
import { getLongAddressName, useAuthContext } from 'noddi-web';
import { useCurrentBrand } from '../stores/BrandStore/brandStore';
import {
  useBookingProposalAddress,
  useBookingProposalItems,
  useBookingProposalStationaryReservation
} from '../stores/bookingProposalStore';
import { isRecallProposalType } from '../utils/bookingProposalType';
import { toCheckoutDiscountRows } from '../utils/checkoutDiscounts';
import { checkoutInvoiceToUserGroupId, deriveProposalContactPhoneState } from '../utils/stationaryCheckout';
import { useMembershipProgramImages } from './useMembershipProgramImages';
import { useProposalCatalogPrices } from './useProposalCatalogPrices';
import { useProposalOpenOrder } from './useProposalOpenOrder';
import { useStationaryPaymentChoice } from './useStationaryPaymentChoice';

type OrderLineItem = {
  key: string;
  name: string;
  quantity: number;
  grossAmount: MoneyRecord;
  originalGrossAmount?: MoneyRecord | null;
  priceListName?: string | null;
  discountPercent?: number | null;
};

type OrderDiscount = {
  key: string;
  label: string;
  amount: MoneyRecord;
  imageUrl?: string | null;
};

/**
 * Shared checkout derivation for stationary proposal card + summary page.
 * Owns shopping cart, payment option, phone validity, and order line math.
 */
export type StationaryProposalCheckout = ReturnType<typeof useStationaryProposalCheckout>;

export function useStationaryProposalCheckout(proposal: BookingProposalRecord | undefined) {
  const { userData, isLoggedIn } = useAuthContext();
  const address = useBookingProposalAddress();
  const bookingProposalItems = useBookingProposalItems();
  const stationaryReservation = useBookingProposalStationaryReservation();
  const { currentBrand } = useCurrentBrand();

  const serviceOrganizationId = proposal?.serviceDepartment.serviceOrganizationId;
  const isOrganization = !!proposal?.organization;
  // Asserted at the query inputs below: both queries are gated on `proposal`,
  // and passing undefined through (never a 0 sentinel) keeps real bugs visible.
  const proposalServiceDepartmentId = proposal?.serviceDepartment.id;
  const proposalUserGroupId = proposal?.userGroupMember.userGroup.id;

  const proposalUserPhone = proposal?.userGroupMember.user.phoneNumber ?? '';
  // Always the proposal contact — never the logged-in / impersonated session user.
  const customerName = proposal
    ? proposal.organization
      ? (proposal.carManager?.name ??
        `${proposal.userGroupMember.user.firstName} ${proposal.userGroupMember.user.lastName}`)
      : `${proposal.userGroupMember.user.firstName} ${proposal.userGroupMember.user.lastName}`
    : undefined;
  const proposalPhoneState = deriveProposalContactPhoneState({
    proposalPhone: proposalUserPhone,
    // Backend classification is authoritative for mobile versus fixed line.
    proposalInvalidPhone: proposal?.invalidPhone,
    proposalUserId: proposal?.userGroupMember.user.id,
    signedInUserId: userData?.user.id
  });

  const carsWithSalesItems = bookingProposalItems.map((item) => ({
    carId: item.car.id,
    selectedSalesItems: item.salesItemsWithQuantity.map(({ salesItem, quantity }) => ({
      salesItemId: salesItem.id,
      quantity
    }))
  }));

  const adHocPrices = buildAdHocPriceMap(bookingProposalItems);
  const {
    priceByCartKey,
    prices: catalogPrices,
    isPending: isCatalogPricesPending
  } = useProposalCatalogPrices(proposal);
  // Outstanding items on the car's open order: the customer pays them with this
  // booking, so they belong in the summary and the total the CTA quotes.
  const { includedOrderLines, isLoading: isOpenOrderLoading } = useProposalOpenOrder(proposal);

  const {
    data: shoppingCartResponse,
    isPending: isShoppingCartPending,
    error: shoppingCartError
  } = noddiAsync.usePostAsGetQuery({
    type: URLKeys.postGetShoppingCartForNewBookingWithoutAddress,
    input: {
      serviceDepartmentId: proposalServiceDepartmentId!,
      carsWithSalesItems,
      userGroupId: proposalUserGroupId,
      brandDomain: currentBrand
    },
    queryConfig: { enabled: !!proposal }
  });

  const shoppingCart = shoppingCartResponse?.data;

  const paymentOptionsEnabled = isLoggedIn && !!serviceOrganizationId && !!proposal && proposalUserGroupId != null;
  const proposalCarIds = proposal?.bookingProposalItems.map((item) => item.car.id) ?? [];
  const {
    data: paymentOptionsData,
    error: paymentOptionsError,
    isLoading: isPaymentOptionsLoading,
    isFetching: isPaymentOptionsFetching,
    refetch: refetchPaymentOptions
  } = noddiAsync.useGet({
    type: URLKeys.getPaymentOptions,
    input: {
      userGroupId: proposalUserGroupId!,
      serviceOrganizationId: serviceOrganizationId!,
      serviceDepartmentId: proposalServiceDepartmentId,
      carIds: proposalCarIds.length > 0 ? proposalCarIds : undefined
    },
    queryConfig: {
      enabled: paymentOptionsEnabled
    }
  });

  const { paymentOption, setPaymentOption, isInvoice, showPaymentChoice } = useStationaryPaymentChoice({
    isOrganization,
    paymentOptions: paymentOptionsData?.paymentOptions
  });
  const invoiceTo = paymentOptionsData?.invoiceTo ?? null;
  const organizationName = proposal?.userGroupMember.userGroup.name ?? '';
  const invoiceRecipientName = invoiceTo?.name || organizationName || customerName || '';
  const invoiceToUserGroupId = checkoutInvoiceToUserGroupId(isInvoice, invoiceTo);
  const organizationId = proposal?.organization?.id ?? null;
  const registrationNumber = proposal?.organization?.registrationNumber;
  // Org number is only required for invoice; pay-before / pay-after skip it.
  const requiresOrganizationNumber = isInvoice && isOrganization && !registrationNumber;
  const isRecallProposal = proposal != null && isRecallProposalType(proposal.type.value);

  // Public location endpoint — summary shows the workshop address before login.
  // Do not use getServiceOrganization here (authenticated; 401 for anonymous).
  const { data: serviceOrganizationLocation } = noddiAsync.useGet({
    type: URLKeys.getServiceOrganizationLocation,
    input: { pk: serviceOrganizationId! },
    queryConfig: {
      enabled: !!serviceOrganizationId
    }
  });

  // Prefer the pinned garage site; fall back to the service organization location.
  const locationName =
    proposal?.garage?.name ?? proposal?.serviceDepartment.userGroup.name ?? serviceOrganizationLocation?.name ?? '';
  const locationAddressRecord = proposal?.garage?.address ?? serviceOrganizationLocation?.address;
  const locationAddress = locationAddressRecord
    ? {
        displayText: getLongAddressName(locationAddressRecord),
        googleMapsUrl: locationAddressRecord.googleMapsUrl
      }
    : undefined;
  const addressCountryCode =
    locationAddressRecord?.countryCode ?? address?.countryCode ?? serviceOrganizationLocation?.address?.countryCode;

  const bookingUserGroupId = proposalUserGroupId ?? null;

  const { imageByDiscountId } = useMembershipProgramImages(proposalUserGroupId);
  const { orderLineItems, orderDiscounts, orderTotalPrice, carDisplayName } = deriveOrderTotals(
    shoppingCart,
    adHocPrices,
    priceByCartKey,
    imageByDiscountId,
    includedOrderLines
  );

  return {
    isLoggedIn,
    /** Site brand the proposal is being confirmed under (brand-scoped rules). */
    brand: currentBrand,
    addressCountryCode,
    bookingProposalItems,
    catalogPrices,
    stationaryReservation,
    isShoppingCartPending: isShoppingCartPending || isCatalogPricesPending || isOpenOrderLoading,
    shoppingCartError: shoppingCartError ?? null,
    isPaymentOptionsLoading: paymentOptionsEnabled && isPaymentOptionsLoading,
    paymentOptionsError: paymentOptionsError ?? null,
    isPaymentOptionsFetching,
    refetchPaymentOptions,
    paymentOption,
    setPaymentOption,
    isInvoice,
    showPaymentChoice,
    invoiceTo,
    invoiceRecipientName,
    invoiceToUserGroupId,
    isOrganization,
    organizationName,
    organizationId,
    registrationNumber,
    requiresOrganizationNumber,
    isRecallProposal,
    locationName,
    locationAddress,
    customerName,
    customerPhoneNumber: proposalUserPhone || undefined,
    hasValidContactPhone: proposalPhoneState.hasValidContactPhone,
    canUpdateContactPhone: proposalPhoneState.canUpdateContactPhone,
    isLandline: proposalPhoneState.isLandline,
    displayedInvalidUserPhone: proposalPhoneState.invalidUserPhoneDisplay,
    /** Logged out and the contact number cannot receive the sign-in SMS. */
    showsLoggedOutContactPhoneNotice: !isLoggedIn && !!proposal && !proposalPhoneState.hasValidContactPhone,
    orderLineItems,
    /** Open-order items added at checkout, for the pre-checkout inclusions block. */
    includedOrderLines,
    orderDiscounts,
    orderTotalPrice,
    carDisplayName,
    customerIsDriver: proposal?.carManager?.id != null && proposal.userGroupMember.user.id === proposal.carManager.id,
    totalAmount: orderTotalPrice.amount,
    bookingUserGroupId
  };
}

function buildAdHocPriceMap(bookingProposalItems: BookingProposalItemRecord[]) {
  const adHocPrices = new Map<string, MoneyRecord>();
  for (const item of bookingProposalItems) {
    for (const { salesItem, priceAdHoc } of item.salesItemsWithQuantity) {
      if (priceAdHoc) {
        adHocPrices.set(catalogPriceMapKey(item.car.licensePlate.number, salesItem.id), priceAdHoc.priceGross);
      }
    }
  }
  return adHocPrices;
}

function deriveOrderTotals(
  shoppingCart: ShoppingCartRecord | undefined,
  adHocPrices: Map<string, MoneyRecord>,
  catalogPriceByKey: Map<string, ProposalCatalogPrice>,
  imageByDiscountId: Map<number, string>,
  includedOrderLines: IncludedOrderLine[]
) {
  const cartLineItems: OrderLineItem[] = shoppingCart
    ? shoppingCart.cars.flatMap((car) =>
        [...car.salesItems]
          .sort((a, b) => b.price.priceGross.amount - a.price.priceGross.amount)
          .map((item) => {
            const key = catalogPriceMapKey(car.licensePlate.number, item.salesItemId);
            const catalog = catalogPriceByKey.get(key);
            const unitPrice = catalog?.grossPrice ?? adHocPrices.get(key) ?? item.price.priceGross;
            const quantity = item.selectedQuantity ?? 1;
            return {
              key,
              name: item.name,
              quantity,
              grossAmount: { ...unitPrice, amount: unitPrice.amount * quantity },
              originalGrossAmount: catalog?.originalGrossPrice
                ? { ...catalog.originalGrossPrice, amount: catalog.originalGrossPrice.amount * quantity }
                : null,
              priceListName: catalog?.priceListName,
              discountPercent: catalog?.discountPercent
            };
          })
      )
    : [];

  // Appended, not merged: these lines are not a choice on this booking, and the
  // open order already carries their final price (no catalog match to apply).
  const orderLineItems: OrderLineItem[] = [
    ...cartLineItems,
    ...includedOrderLines.map((line) => ({
      key: line.key,
      name: line.name,
      quantity: line.quantity ?? 1,
      grossAmount: line.grossAmount
    }))
  ];

  const discountRows = shoppingCart
    ? toCheckoutDiscountRows({
        couponDiscounts: shoppingCart.couponDiscounts,
        membershipDiscounts: shoppingCart.membershipDiscounts,
        imageByDiscountId
      })
    : [];
  const totalDiscount = discountRows.reduce((acc, row) => acc + Math.abs(row.amount.amount), 0);

  const orderDiscounts: OrderDiscount[] | undefined =
    discountRows.length > 0
      ? discountRows.map((row) => ({
          key: row.key,
          label: row.label,
          amount: row.amount,
          imageUrl: row.imageUrl
        }))
      : undefined;

  const computedTotal = shoppingCart
    ? orderLineItems.reduce((acc, item) => acc + item.grossAmount.amount, 0) - totalDiscount
    : 0;

  // Explicit type instead of an inferred union: without a cart there is no
  // currency to report, and consumers must see that rather than get a money
  // object whose currency silently disappears.
  const orderTotalPrice: { amount: number; currency?: Currency } = shoppingCart
    ? { amount: computedTotal, currency: shoppingCart.totalPrice.currency }
    : { amount: 0 };

  const carDisplayName = shoppingCart?.cars[0] ? getCarDisplayName({ car: shoppingCart.cars[0], showYear: false }) : '';

  return { orderLineItems, orderDiscounts, orderTotalPrice, carDisplayName };
}
